import { internalLogger } from '../../interface/v1/clientOsLogger';
import bindAllMethods from '../../utils/bindAllMethods';
import injectOneTrust from './injectOneTrust';

import { SetServiceDependencies } from '../../infra/commonInitializer/types';
import { IAnalyticsService } from '../AnalyticsService';
import { ValueStoreFacade } from '../JWeb/facades';
import { INavigationService } from '../navigationService';

import { OPT_IN_GROUP_VALUE, OPT_OUT_GROUP_VALUE } from '../AnalyticsService';
import { ReservedValueStoreKeys } from '../JWeb/JWebEnums';
import { RouteServiceInputType, RoutesService } from '../RoutesService';
import IApplicationService from '../applicationService/IApplicationService';
import {
  LOGGEDIN_PATHNAME,
  LOGGEDOUT_PATHNAME,
  LOGIN_PATHNAME
} from '../session/constants';
import IConsentService from './IConsentService';
import {
  EventHandlerType,
  getOptanonConsentObjectValues,
  registerOptanonDataLayerEventHandler
} from './optanonUtils';
import { ISessionService } from '../session';
import { IABTestingService } from '../ABTestingService';
import { IRouteStageService } from '../RouteStageService';
import { Refreshable } from '../../utils/refreshable/IRefreshable';

export type ConsentServiceInputType = {
  isEmailGathering?: boolean;
  isNative: boolean;
  globalInjectConsentBanner?: boolean;
};

/***
 *  The Consent Service is only used in Web envs.
 */
export default class ConsentService
  extends Refreshable
  implements IConsentService
{
  private _analyticsService: IAnalyticsService;
  private _navigationService: INavigationService;
  private _applicationService: IApplicationService;
  private _routesService: RoutesService;
  private _sessionService: ISessionService;
  private _abTestingService: IABTestingService;
  private _routeStageService: IRouteStageService;
  private _isEmailGathering: boolean;
  private _isNative: boolean;
  private globalInjectConsentBanner: boolean;
  private lastOptInGroups = undefined;

  constructor(consentServiceParams: ConsentServiceInputType) {
    super();
    this._isEmailGathering = consentServiceParams.isEmailGathering;
    this._isNative = consentServiceParams.isNative;
    this.globalInjectConsentBanner =
      consentServiceParams.globalInjectConsentBanner;
    this.setRefreshMethods({
      init: this.init.bind(this),
      refresh: this.refresh.bind(this)
    });

    bindAllMethods(this);
  }

  public setDependencies({ services }: SetServiceDependencies): void {
    this._navigationService = services.navigationService;
    this._analyticsService = services.analyticsService;
    this._applicationService = services.applicationService;
    this._routesService = services.routesService;
    this._sessionService = services.sessionService;
    this._abTestingService = services.abTestingService;
    this._routeStageService = services.routeStageService;

    bindAllMethods(this);
  }

  public async init(): Promise<void> {
    if (this._isNative) return;

    this._registerOnDataLayer();
    this.applyConsentBanner();

    // Initialize lastOptInGroups
    this.lastOptInGroups = (window as any)._O?.getOptInGroups();
  }

  public async refresh(): Promise<void> {
    if (this._isNative) return;
    this.applyConsentBanner();
  }

  public applyConsentBanner(): void {
    if (this._isNative) return;

    const isAnalyticsEnabled = this._analyticsService?.isEnabled?.();

    const isABTestingEnabled = this?._abTestingService?.isEnabled();

    // TODO: This should be received from commons adapter on constructor.
    // this._isConsentEnabled = isAnalyticsEnabled || isOptimizelyEnabled;
    if (isAnalyticsEnabled || isABTestingEnabled) {
      this._injectConsentBanner();

      // Creating listener to be trigged when changes the route
      // TODO: this should be a method for subscribe of NavigationService
      this._navigationService.listen(this._injectConsentBanner);
    }

    internalLogger?.debug?.('Consent Banner flow finished.');
  }

  /*** This method is used just to keep up-to-date the consent in value store. */
  public async updateOptInGroupsInValueStore(): Promise<void> {
    const currentOptInGroups = (window as any)._O?.getOptInGroups();
    if (this.lastOptInGroups != currentOptInGroups) {
      await this._writeOptanonConsentInValueStore();
      this.lastOptInGroups = currentOptInGroups;
    }
  }

  private _shouldInjectConsentBanner(
    currentRoute: RouteServiceInputType
  ): boolean {
    return !(
      this.globalInjectConsentBanner === false ||
      currentRoute?.injectConsentBanner === false
    );
  }

  private _injectConsentBanner(): void {
    const currentPath: string = this._navigationService?.location?.pathname;
    const currentRoute: RouteServiceInputType =
      this._routesService.findRouteByPath(currentPath);

    // check if the current route has a configuration to explicit not inject consent banner.
    const shouldInjectConsentBanner =
      this._shouldInjectConsentBanner(currentRoute);

    if (!shouldInjectConsentBanner) {
      return;
    }

    const isLoginPath = LOGIN_PATHNAME === currentPath;
    const isLoggedinPath = LOGGEDIN_PATHNAME === currentPath;
    const isLoggedoutPath = LOGGEDOUT_PATHNAME === currentPath;

    const isLoggedinOrLoggedoutPath = isLoggedinPath || isLoggedoutPath;
    const isLoginPathWithoutEmailGathering =
      isLoginPath && !this._isEmailGathering;

    // not inject if the path is the loggedin or loggedout paths or the current path is the login path with no email gathering enabled.
    if (isLoggedinOrLoggedoutPath || isLoginPathWithoutEmailGathering) return;

    const stagePath = this._routeStageService.getStageByPath(currentPath);
    const isPrivateStagePathWithUserLoggedOut =
      stagePath && !stagePath?.public && !this?._sessionService?.isLoggedIn();

    const isPrivateRoutePathWithUserLoggedOut =
      currentRoute &&
      !currentRoute?.public &&
      !this?._sessionService?.isLoggedIn();

    // not inject if the current path is a stage or route path that is not public and the user is not logged in.
    if (
      isPrivateStagePathWithUserLoggedOut ||
      isPrivateRoutePathWithUserLoggedOut
    )
      return;

    injectOneTrust(this._applicationService.getPortalStack());
  }

  private async _writeOptanonConsentInValueStore(): Promise<void> {
    if (this._isNative) {
      return;
    }
    const { optanonConsentId, optanonConsent } =
      getOptanonConsentObjectValues();

    await ValueStoreFacade.writeDataToValueStore([
      {
        key: ReservedValueStoreKeys.visitorUuid,
        value: optanonConsentId
      },
      {
        key: ReservedValueStoreKeys.webApplicationConsent,
        value: optanonConsent
      },
      {
        key: ReservedValueStoreKeys.applicationInstanceId,
        value: optanonConsentId
      }
    ]);
  }

  private _registerOnDataLayer(): void {
    const writeOptanonConsentInValueStoreOnOptIn: EventHandlerType = {
      event: 'pb.onOptInGroups',
      callback: this._writeOptanonConsentInValueStore,
      groups: OPT_IN_GROUP_VALUE
    };

    const writeOptanonConsentInValueStoreOnOptOut: EventHandlerType = {
      event: 'pb.onOptInGroups',
      callback: this._writeOptanonConsentInValueStore,
      groups: OPT_OUT_GROUP_VALUE
    };

    registerOptanonDataLayerEventHandler(
      writeOptanonConsentInValueStoreOnOptIn
    );
    registerOptanonDataLayerEventHandler(
      writeOptanonConsentInValueStoreOnOptOut
    );
  }
}
